import { Component, OnInit } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { Provider, ProviderService } from 'src/app/services/provider.service';
import { PermissionService } from 'src/app/services/permission.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ProviderInfoCellComponent } from 'src/app/cell-renderer/provider-info-cell/provider-info-cell.component';
import { ProviderActionsComponent } from 'src/app/cell-renderer/provider-actions/provider-actions.component';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';

interface ProviderRow extends Provider {
    avatarBg: string;
    avatarColor: string;
    avatarInitial: string;
}

@Component({
    selector: 'app-provider-list',
    templateUrl: './provider-list.component.html',
    styleUrls: ['./provider-list.component.css']
})
export class ProviderListComponent implements OnInit {
    constructor(
        private providerService: ProviderService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    private readonly avatarPalette = [
        { bg: '#eef2ff', color: '#4f46e5' },
        { bg: '#e6fbf3', color: '#10b981' },
        { bg: '#fffbeb', color: '#d97706' },
        { bg: '#e6f0ff', color: '#0066ff' },
        { bg: '#fdf2f8', color: '#db2777' },
        { bg: '#f5f3ff', color: '#8b5cf6' },
        { bg: '#fff7ed', color: '#ea580c' },
        { bg: '#fee2e2', color: '#ef4444' },
    ];

    filterText = '';
    rowData: ProviderRow[] = [];
    private gridApi!: GridApi;

    // Mismo paginado (diseño y comportamiento) que "Unidades por liberar"
    // (departure-check-dashboard), pero manejado del lado del cliente ya
    // que ag-grid trae todo el listado de proveedores de una sola vez.
    perPage = 10;
    currentPage = 1;
    lastPage = 1;
    total = 0;

    columnDefs: any[] = [
        { headerName: 'Proveedor', field: 'trade_name', cellRenderer: ProviderInfoCellComponent, minWidth: 220, flex: 1 },
        { headerName: 'Razón social', field: 'business_name', minWidth: 200, flex: 1 },
        { headerName: 'RFC', field: 'rfc', width: 150 },
        { headerName: 'Ciudad', field: 'city', width: 150, valueFormatter: (p: any) => p.value || '—' },
        { headerName: 'Acciones', cellRenderer: ProviderActionsComponent, width: 110, sortable: false, filter: false, resizable: false },
    ];

    defaultColDef: any = {
        sortable: true,
        filter: false,
    };

    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams: any = {
        loadingMessage: 'Espere un momento...',
    };

    public overlayNoRowsTemplate = '<span class="provider-table-empty">No hay proveedores para mostrar.</span>';

    canDo(module: string, action: string): boolean {
        return this.permissionService.canDo(module, action);
    }

    /** Enlazado al botón "Actualizar" del topbar compartido (ver dashboard.component.ts refreshGrid). */
    refreshGrid(): void {
        this.load();
    }

    ngOnInit(): void {
        this.load();
    }

    onGridReady(params: any): void {
        this.gridApi = params.api;
        this.onPaginationChanged();
    }

    onSearchChange(): void {
        this.gridApi?.setGridOption('quickFilterText', this.filterText);
        this.gridApi?.paginationGoToPage(0);
    }

    onPaginationChanged(): void {
        if (!this.gridApi) return;
        this.currentPage = this.gridApi.paginationGetCurrentPage() + 1;
        this.lastPage = Math.max(1, this.gridApi.paginationGetTotalPages());
        this.total = this.gridApi.paginationGetRowCount();
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.lastPage || !this.gridApi) return;
        this.gridApi.paginationGoToPage(page - 1);
    }

    /** Ventana de a lo más 3 botones de página, centrada en la actual. */
    get pageNumbers(): number[] {
        const windowSize = 3;
        const end = Math.min(this.lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    /** "Mostrando X a Y de Z proveedores". */
    get paginationRangeLabel(): string {
        if (this.total === 0) return 'Mostrando 0 proveedores';
        const start = (this.currentPage - 1) * this.perPage + 1;
        const end = Math.min(this.currentPage * this.perPage, this.total);
        return `Mostrando ${start} a ${end} de ${this.total} proveedores`;
    }

    private toRow(p: Provider): ProviderRow {
        const palette = this.avatarPalette[(p.id || 0) % this.avatarPalette.length];
        return {
            ...p,
            avatarBg: palette.bg,
            avatarColor: palette.color,
            avatarInitial: (p.trade_name || p.business_name || '?').trim().charAt(0).toUpperCase(),
        };
    }

    load(): void {
        this.spinner.show();
        this.providerService.listProviders().subscribe({
            next: (resp: any) => {
                this.rowData = ((resp?.data || []) as Provider[]).map(p => this.toRow(p));
                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el listado de proveedores', icon: 'error' });
            }
        });
    }
}
